/**
 * ToolHandler — Handles Realtime API tool calls (function calls).
 *
 * Accumulates streamed function_call_arguments deltas, parses on completion,
 * dispatches to the page agent Cloud Function, and executes returned actions.
 *
 * Follows the pattern from ai-canvas useToolDataChannel.ts.
 */

import type { RealtimeSession } from './realtimeSession'
import type { DomAnalyzer } from './domAnalyzer'
import { PageActions } from '../actions/pageActions'
import { OverlayActions } from '../actions/overlayActions'
import { ConfirmationPrompt } from '../rendering/confirmationPrompt'
import { postPageAction } from '../api/backend'

// Actions that require visitor confirmation before executing
const CONFIRM_ACTIONS = new Set(['scroll_to', 'click', 'highlight'])

export interface PageActionCommand {
  action: 'scroll_to' | 'highlight' | 'click' | 'fill' | 'describe'
  index: number
  value?: string
  description: string
}

export class ToolHandler {
  private realtimeSession: RealtimeSession
  private domAnalyzer: DomAnalyzer
  private pageActions: PageActions
  private overlayActions: OverlayActions
  private confirmationPrompt: ConfirmationPrompt
  private staffId: string
  private backendUrl: string

  /** Called when a tool call completes (for logging). */
  onToolCall: ((tool: string, args: Record<string, unknown>) => void) | null = null

  // Accumulation state (per item_id)
  private fnArgBufs = new Map<string, string>()
  private fnNames = new Map<string, string>()
  private fnCallIds = new Map<string, string>()

  constructor(opts: {
    realtimeSession: RealtimeSession
    domAnalyzer: DomAnalyzer
    pageActions: PageActions
    overlayActions: OverlayActions
    confirmationPrompt: ConfirmationPrompt
    staffId: string
    backendUrl: string
  }) {
    this.realtimeSession = opts.realtimeSession
    this.domAnalyzer = opts.domAnalyzer
    this.pageActions = opts.pageActions
    this.overlayActions = opts.overlayActions
    this.confirmationPrompt = opts.confirmationPrompt
    this.staffId = opts.staffId
    this.backendUrl = opts.backendUrl
  }

  /**
   * Handle a data channel message. Returns true if the message was a tool call
   * event that was handled, false if it should be processed by other handlers.
   */
  handleMessage(msg: any): boolean {
    const type = msg.type as string

    switch (type) {
      case 'response.output_item.added':
        if (msg.item?.type === 'function_call') {
          const itemId = msg.item.id
          const name = msg.item.name
          const callId = msg.item.call_id || msg.item.id
          this.fnNames.set(itemId, name)
          this.fnCallIds.set(itemId, callId)
          this.fnArgBufs.set(itemId, '')
          console.log(`[ToolHandler] Function call started: ${name} (${itemId})`)
          return true
        }
        return false

      case 'response.function_call_arguments.delta':
        if (msg.item_id && this.fnArgBufs.has(msg.item_id)) {
          const current = this.fnArgBufs.get(msg.item_id) ?? ''
          this.fnArgBufs.set(msg.item_id, current + (msg.delta ?? ''))
          return true
        }
        return false

      case 'response.function_call_arguments.done':
        if (msg.item_id && this.fnArgBufs.has(msg.item_id)) {
          this.handleFunctionCallDone(msg.item_id)
          return true
        }
        return false

      default:
        return false
    }
  }

  // -----------------------------------------------------------------------
  // Private
  // -----------------------------------------------------------------------

  private async handleFunctionCallDone(itemId: string): Promise<void> {
    const name = this.fnNames.get(itemId) ?? 'unknown'
    const callId = this.fnCallIds.get(itemId) ?? itemId
    const argsStr = this.fnArgBufs.get(itemId) ?? '{}'

    // Clean up accumulation state
    this.fnArgBufs.delete(itemId)
    this.fnNames.delete(itemId)
    this.fnCallIds.delete(itemId)

    // Parse arguments
    let args: Record<string, any>
    try {
      args = JSON.parse(argsStr)
    } catch {
      console.warn(`[ToolHandler] Failed to parse args for ${name}:`, argsStr)
      this.sendToolResult(callId, { error: 'Malformed JSON in function call arguments' })
      return
    }

    console.log(`[ToolHandler] Executing: ${name}`, args)

    // Notify logger
    this.onToolCall?.(name, args)

    let result: object | null = null

    switch (name) {
      case 'page_action':
        await this.handlePageAction(callId, args.instruction ?? '')
        return // handlePageAction sends its own result

      case 'show_text_card':
        result = this.overlayActions.showTextCard(args.title ?? '', args.body ?? '')
        break

      case 'show_image_card':
        result = this.overlayActions.showImageCard(args.image_url ?? '', args.caption)
        break

      case 'show_bullet_list':
        result = this.overlayActions.showBulletList(args.title ?? '', args.items ?? [])
        break

      case 'dismiss_overlays':
        result = this.overlayActions.dismissAll()
        break

      default:
        console.warn(`[ToolHandler] Unknown tool: ${name}`)
        result = { error: `Unknown tool: ${name}` }
    }

    this.sendToolResult(callId, result!)
  }

  private async handlePageAction(callId: string, instruction: string): Promise<void> {
    try {
      // 1. Analyze the DOM
      const { text: domSnapshot } = this.domAnalyzer.analyze()
      console.log(`[ToolHandler] DOM snapshot: ${domSnapshot.length} chars, instruction: "${instruction}"`)

      // 2. Call the page agent Cloud Function
      const response = await postPageAction(
        this.staffId,
        domSnapshot,
        instruction,
        this.backendUrl,
      )

      const actions = (response.actions ?? []) as PageActionCommand[]
      console.log(`[ToolHandler] Page agent returned ${actions.length} actions:`, actions)

      // 3. Execute each action
      const results: Array<{ action: string; status: string; description: string }> = []

      for (const cmd of actions) {
        if (cmd.action === 'describe') {
          results.push({ action: 'describe', status: 'ok', description: cmd.description })
          continue
        }

        // Actions requiring confirmation
        if (CONFIRM_ACTIONS.has(cmd.action)) {
          const confirmed = await this.confirmationPrompt.ask(
            `${cmd.description}?`
          )
          if (!confirmed) {
            results.push({
              action: cmd.action,
              status: 'declined',
              description: 'Visitor declined',
            })
            continue
          }
        }

        // Execute
        let result: { status: string; description: string }
        switch (cmd.action) {
          case 'scroll_to':
            result = this.pageActions.scrollTo(cmd.index)
            break
          case 'highlight':
            result = this.pageActions.highlight(cmd.index)
            break
          case 'click':
            result = this.pageActions.click(cmd.index)
            break
          case 'fill':
            result = this.pageActions.fill(cmd.index, cmd.value ?? '')
            break
          default:
            result = { status: 'error', description: `Unknown action: ${cmd.action}` }
        }
        results.push({ action: cmd.action, ...result })
      }

      this.sendToolResult(callId, { results })

    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      console.error('[ToolHandler] Page action failed:', msg)
      this.sendToolResult(callId, { error: msg })
    }
  }

  private sendToolResult(callId: string, output: object): void {
    this.realtimeSession.sendJSON({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: JSON.stringify(output),
      },
    })
    // Trigger the AI's next response
    this.realtimeSession.sendJSON({ type: 'response.create' })
  }
}
